/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useReducer, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { TracesIDContext } from '@/context';
import { request } from '@/services/request';
import {
  PAGINATED_FACT_NAMES,
  TRACE_FETCH_PAGE_SIZE,
  traceID,
  traces,
} from '@/utils/constants';
import { TFactMeasure } from 'types/traces';

interface TraceByIDState {
  traceIdSearchData: any;
  traceIdError: any;
  traceIdLoading: boolean;
  factMeasures: TFactMeasure | null;
  factMeasuresLoading: boolean;
  // Per-trace fact measures (e.g. token counts) keyed by trace_id, used by the
  // agent_session tree view to show each trace's tokens next to its duration.
  traceTokenMeasures: TFactMeasure | null;
  traceTokenMeasuresLoading: boolean;
  // Server-pagination of a fact's traces (mirrors TracesProvider).
  traceIdNextPageToken: string | null;
  traceIdLoadingMore: boolean;
  traceIdFactCount: number | null;
}

type TraceIDProviderProps = Readonly<{
  children: React.ReactNode;
  appName?: string;
  workflowName?: string;
}>;

const initialState: TraceByIDState = {
  traceIdSearchData: [],
  traceIdError: null,
  traceIdLoading: false,
  factMeasures: null,
  factMeasuresLoading: false,
  traceTokenMeasures: null,
  traceTokenMeasuresLoading: false,
  traceIdNextPageToken: null,
  traceIdLoadingMore: false,
  traceIdFactCount: null,
};

const {
  FETCH_TRACES_BY_ID,
  TRACE_ID_ERROR,
  TRACE_ID_LOADING,
  CLEAR_TRACE_ID_RESULTS,
  TRACE_ID_LOADING_MORE,
  FETCH_MORE_TRACES_BY_ID,
  SET_TRACE_ID_PAGE_META,
} = traceID;

const {
  FETCH_FACT_MEASURES,
  FACT_MEASURES_LOADING,
  FETCH_TRACE_TOKEN_MEASURES,
  TRACE_TOKEN_MEASURES_LOADING,
} = traces;

// 60 seconds.
const CACHE_TTL_MS = 60_000;

type CacheEntry<T> = {
  data: T;
  timestamp: number;
};

// Cache structure: call the API after 60 seconds or if there is no cache for the given trace_id and factName.

const getCachedValue = <T,>(cache: Map<string, CacheEntry<T>>, key: string) => {
  const cached = cache.get(key);

  if (!cached) return undefined;

  if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
    return undefined;
  }

  return cached.data;
};

const reducer = (
  state: TraceByIDState,
  action: { type: string; payload?: any }
): TraceByIDState => {
  switch (action.type) {
    case TRACE_ID_LOADING:
      return { ...state, traceIdLoading: action.payload };
    case FETCH_TRACES_BY_ID:
      return { ...state, traceIdSearchData: action.payload };
    case SET_TRACE_ID_PAGE_META:
      return {
        ...state,
        traceIdNextPageToken: action.payload?.nextPageToken ?? null,
        traceIdFactCount: action.payload?.factCount ?? null,
      };
    case FETCH_MORE_TRACES_BY_ID:
      return {
        ...state,
        traceIdSearchData: [
          ...(Array.isArray(state.traceIdSearchData)
            ? state.traceIdSearchData
            : []),
          ...action.payload.traces,
        ],
        traceIdNextPageToken: action.payload?.nextPageToken ?? null,
        traceIdFactCount: action.payload?.factCount ?? state.traceIdFactCount,
      };
    case TRACE_ID_LOADING_MORE:
      return { ...state, traceIdLoadingMore: action.payload };
    case TRACE_ID_ERROR:
      return { ...state, traceIdError: action.payload };
    case CLEAR_TRACE_ID_RESULTS:
      return {
        ...state,
        traceIdSearchData: [],
        traceIdError: null,
        traceIdNextPageToken: null,
        traceIdFactCount: null,
      };
    case FETCH_FACT_MEASURES:
      return { ...state, factMeasures: action.payload };
    case FACT_MEASURES_LOADING:
      return { ...state, factMeasuresLoading: action.payload };
    case FETCH_TRACE_TOKEN_MEASURES:
      return { ...state, traceTokenMeasures: action.payload };
    case TRACE_TOKEN_MEASURES_LOADING:
      return { ...state, traceTokenMeasuresLoading: action.payload };
    default:
      return state;
  }
};

// This Provider is used for the search by trace-id functionality
const TraceIDProvider = ({
  children,
  appName,
  workflowName,
}: TraceIDProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const router = useRouter();
  const searchParams = useSearchParams();
  const requestIdRef = useRef(0);
  const traceCacheRef = useRef<Map<string, CacheEntry<any[]>>>(new Map());
  // Parallel cache for pagination meta (cursor + total) keyed like traceCacheRef,
  // so a cached re-search restores the cursor instead of losing it.
  const tracePageMetaCacheRef = useRef<
    Map<string, { nextPageToken: string | null; factCount: number | null }>
  >(new Map());
  const factMeasuresCacheRef = useRef<
    Map<string, CacheEntry<TFactMeasure | null>>
  >(new Map());
  const inFlightTracesRef = useRef<Map<string, Promise<any[]>>>(new Map());
  const inFlightFactMeasuresRef = useRef<
    Map<string, Promise<TFactMeasure | null>>
  >(new Map());
  const traceTokenMeasuresCacheRef = useRef<
    Map<string, CacheEntry<TFactMeasure | null>>
  >(new Map());
  const inFlightTraceTokenMeasuresRef = useRef<
    Map<string, Promise<TFactMeasure | null>>
  >(new Map());

  const endpoint = workflowName
    ? `workflows/${workflowName}/traces`
    : `apps/${appName}/traces`;

  const fetchTraceByID = async (
    trace_id: string,
    factName: string,
    shouldRedirect = true
  ) => {
    const currentRequestId = ++requestIdRef.current;
    const resolvedFact = factName || 'traces';
    const isPaginatedFact = PAGINATED_FACT_NAMES.includes(resolvedFact);
    const cacheKey = `${endpoint}:${resolvedFact}:${trace_id}`;
    const staleCachedTraces = traceCacheRef.current.get(cacheKey)?.data;

    if (shouldRedirect) {
      const params = new URLSearchParams(Array.from(searchParams.entries()));
      params.set('trace_id', trace_id);
      router.push(`?${params.toString()}`);
    }

    const cachedTraces = getCachedValue(traceCacheRef.current, cacheKey);
    if (cachedTraces !== undefined) {
      const cachedMeta = tracePageMetaCacheRef.current.get(cacheKey) ?? {
        nextPageToken: null,
        factCount: null,
      };
      dispatch({ type: TRACE_ID_ERROR, payload: null });
      dispatch({ type: FETCH_TRACES_BY_ID, payload: cachedTraces });
      dispatch({ type: SET_TRACE_ID_PAGE_META, payload: cachedMeta });
      dispatch({ type: TRACE_ID_LOADING, payload: false });

      if (shouldRedirect) {
        const params = new URLSearchParams(window.location.search);
        params.set('trace_id', trace_id);
        if (!params.get('activeTab')) params.set('activeTab', 'search');
        if (!params.get('factName')) params.set('factName', resolvedFact);
        router.replace(`?${params.toString()}`);
      }

      return cachedTraces;
    }

    dispatch({ type: TRACE_ID_LOADING, payload: true });

    try {
      let inFlight = inFlightTracesRef.current.get(cacheKey);
      if (!inFlight) {
        inFlight = (async () => {
          try {
            const data = await request.get({
              endpoint: `${endpoint}`,
              queryParams: {
                duration_fact: resolvedFact,
                fact_ids: trace_id,
                // Paginated facts fetch a page at a time; the tree loads more via
                // the next_page_token cursor (see loadMoreTracesByID).
                ...(isPaginatedFact
                  ? { page_size: String(TRACE_FETCH_PAGE_SIZE) }
                  : {}),
              },
            });
            const response = Array.isArray(data) ? data[0] : data;
            const traces = Array.isArray(response?.traces)
              ? response.traces
              : [];
            traceCacheRef.current.set(cacheKey, {
              data: traces,
              timestamp: Date.now(),
            });
            // next_page_token + fact_count only appear while more pages remain.
            tracePageMetaCacheRef.current.set(cacheKey, {
              nextPageToken: response?.next_page_token ?? null,
              factCount: response?.fact_count ?? null,
            });
            return traces;
          } finally {
            inFlightTracesRef.current.delete(cacheKey);
          }
        })();
        inFlightTracesRef.current.set(cacheKey, inFlight);
      }

      const traces = await inFlight;
      if (currentRequestId !== requestIdRef.current) return [];

      if (shouldRedirect) {
        const params = new URLSearchParams(window.location.search);
        params.set('trace_id', trace_id);
        if (!params.get('activeTab')) params.set('activeTab', 'search');
        if (!params.get('factName')) params.set('factName', resolvedFact);
        router.replace(`?${params.toString()}`);
      }

      dispatch({ type: TRACE_ID_ERROR, payload: null });
      dispatch({ type: FETCH_TRACES_BY_ID, payload: traces });
      dispatch({
        type: SET_TRACE_ID_PAGE_META,
        payload: tracePageMetaCacheRef.current.get(cacheKey) ?? {
          nextPageToken: null,
          factCount: null,
        },
      });
      return traces;
    } catch (error) {
      dispatch({ type: TRACE_ID_ERROR, payload: error });

      if (staleCachedTraces !== undefined) {
        dispatch({ type: FETCH_TRACES_BY_ID, payload: staleCachedTraces });
        return staleCachedTraces;
      }

      return [];
    } finally {
      dispatch({ type: TRACE_ID_LOADING, payload: false });
    }
  };

  // Fetch + append the next page of a searched fact's traces via the cursor.
  const loadMoreTracesByID = async (
    trace_id: string,
    factName: string,
    nextPageToken: string | null
  ) => {
    if (!nextPageToken) return;
    const resolvedFact = factName || 'traces';
    dispatch({ type: TRACE_ID_LOADING_MORE, payload: true });
    try {
      const data = await request.get({
        endpoint: `${endpoint}`,
        queryParams: {
          duration_fact: resolvedFact,
          fact_ids: trace_id,
          page_size: String(TRACE_FETCH_PAGE_SIZE),
          next_page_token: nextPageToken,
        },
      });
      const response = Array.isArray(data) ? data[0] : data;
      const newTraces = Array.isArray(response?.traces) ? response.traces : [];
      dispatch({
        type: FETCH_MORE_TRACES_BY_ID,
        payload: {
          traces: newTraces,
          nextPageToken: response?.next_page_token ?? null,
          factCount: response?.fact_count ?? null,
        },
      });
    } catch (error) {
      dispatch({ type: TRACE_ID_ERROR, payload: error });
    } finally {
      dispatch({ type: TRACE_ID_LOADING_MORE, payload: false });
    }
  };

  const fetchFactMeasures = async (
    factName: string,
    filterFactIds?: string | null
  ) => {
    if (!appName || !factName) return;

    const cacheKey = `${appName}:${factName}:${filterFactIds ?? 'all'}`;
    const staleCachedFactMeasures =
      factMeasuresCacheRef.current.get(cacheKey)?.data;
    const cachedFactMeasures = getCachedValue(
      factMeasuresCacheRef.current,
      cacheKey
    );

    if (cachedFactMeasures !== undefined) {
      dispatch({ type: FETCH_FACT_MEASURES, payload: cachedFactMeasures });
      dispatch({ type: FACT_MEASURES_LOADING, payload: false });
      return { fact_measures: cachedFactMeasures };
    }

    try {
      dispatch({ type: FACT_MEASURES_LOADING, payload: true });

      // Ensure only one in-flight request for the same fact measures to prevent redundant API calls when multiple tooltips are opened simultaneously for the same trace_id and factName.
      let inFlight = inFlightFactMeasuresRef.current.get(cacheKey);
      if (!inFlight) {
        inFlight = (async () => {
          try {
            const queryParams: Record<string, string> = {};
            if (filterFactIds) queryParams.filter_fact_ids = filterFactIds;
            const data = await request.get({
              endpoint: `insights/apps/${appName}/facts/${factName}/fact_measures/${factName}`,
              queryParams,
            });
            const factMeasures = (data[0]?.fact_measures ??
              null) as TFactMeasure | null;
            factMeasuresCacheRef.current.set(cacheKey, {
              data: factMeasures,
              timestamp: Date.now(),
            });
            return factMeasures;
          } finally {
            inFlightFactMeasuresRef.current.delete(cacheKey);
          }
        })();
        inFlightFactMeasuresRef.current.set(cacheKey, inFlight);
      }

      const factMeasures = await inFlight;

      dispatch({
        type: FETCH_FACT_MEASURES,
        payload: factMeasures,
      });
      return { fact_measures: factMeasures };
    } catch (error: any) {
      console.error('Error fetching fact measures:', error);

      if (staleCachedFactMeasures !== undefined) {
        dispatch({
          type: FETCH_FACT_MEASURES,
          payload: staleCachedFactMeasures,
        });
        return { fact_measures: staleCachedFactMeasures };
      }
    } finally {
      dispatch({ type: FACT_MEASURES_LOADING, payload: false });
    }
  };

  // Fetch per-trace token measures for a set of traces in a single call, so the
  // agent_session tree view can show each trace's tokens beside its duration.
  // The response is keyed by trace_id. No time range — consistent with the
  // search-by-id flow's fetchFactMeasures.
  const fetchTraceTokenMeasures = async (traceIds: string[]) => {
    if (!appName || !traceIds?.length) return;

    const filterFactIds = traceIds.join(',');
    const cacheKey = `${appName}:traces:${filterFactIds}`;
    const staleCached = traceTokenMeasuresCacheRef.current.get(cacheKey)?.data;
    const cached = getCachedValue(traceTokenMeasuresCacheRef.current, cacheKey);

    if (cached !== undefined) {
      dispatch({ type: FETCH_TRACE_TOKEN_MEASURES, payload: cached });
      dispatch({ type: TRACE_TOKEN_MEASURES_LOADING, payload: false });
      return { fact_measures: cached };
    }

    try {
      dispatch({ type: TRACE_TOKEN_MEASURES_LOADING, payload: true });

      let inFlight = inFlightTraceTokenMeasuresRef.current.get(cacheKey);
      if (!inFlight) {
        inFlight = (async () => {
          try {
            const data = await request.get({
              endpoint: `insights/apps/${appName}/facts/traces/fact_measures/traces`,
              queryParams: { filter_fact_ids: filterFactIds },
            });
            const measures = (data[0]?.fact_measures ??
              null) as TFactMeasure | null;
            traceTokenMeasuresCacheRef.current.set(cacheKey, {
              data: measures,
              timestamp: Date.now(),
            });
            return measures;
          } finally {
            inFlightTraceTokenMeasuresRef.current.delete(cacheKey);
          }
        })();
        inFlightTraceTokenMeasuresRef.current.set(cacheKey, inFlight);
      }

      const measures = await inFlight;
      dispatch({ type: FETCH_TRACE_TOKEN_MEASURES, payload: measures });
      return { fact_measures: measures };
    } catch (error: any) {
      console.error('Error fetching trace token measures:', error);
      if (staleCached !== undefined) {
        dispatch({ type: FETCH_TRACE_TOKEN_MEASURES, payload: staleCached });
        return { fact_measures: staleCached };
      }
    } finally {
      dispatch({ type: TRACE_TOKEN_MEASURES_LOADING, payload: false });
    }
  };

  const clearTraceIDResults = () => dispatch({ type: CLEAR_TRACE_ID_RESULTS });

  return (
    <TracesIDContext.Provider
      value={{
        state,
        fetchTraceByID,
        loadMoreTracesByID,
        fetchFactMeasures,
        fetchTraceTokenMeasures,
        clearTraceIDResults,
      }}
    >
      {children}
    </TracesIDContext.Provider>
  );
};

export default TraceIDProvider;
